// App
import { shallowEqual, useSelector } from 'react-redux';

// Constellation
import { Box, Heading, TextButton } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';

import pathsManifest from '../../utils/pathsManifest';

import ZestimateCard from '../../components/ZestimateCard';

import HeroImageWebp from 'images/priceMyRentalHero.webp';
import { containerStyles, detailsStyles } from './styles';

// Types
import type React from 'react';
import type { State } from '../../types/State';

const HeadingCard = () => {
  const { fromApp, priceFromFilter, userProperty, min, max } = useSelector(
    (state: State) => ({
      fromApp: state.app.fromApp,
      priceFromFilter: state.filter.price,
      userProperty: state.address,
      min: state.comparables.min,
      max: state.comparables.max,
    }),
    shallowEqual,
  );

  const {
    street,
    beds,
    baths,
    sqft,
    city,
    state,
    zip,
    unit,
    rentZestimate,
    rentZestimateRangeHigh,
    rentZestimateRangeLow,
    zpid,
  } = userProperty;
  const hasUserPropertyData = street !== null;

  const h1Text = hasUserPropertyData ? (
    <>
      {street} {unit && unit !== ' ' ? ', Unit ' + unit : ''}
    </>
  ) : (
    <>
      {' '}
      <br />
      Address not found
    </>
  );

  const h2Text = hasUserPropertyData ? (
    <>
      {city}, {state} {zip}
      <br />
      {beds !== 0 ? <>{beds} bed</> : <>Studio</>} • {baths} bath • {sqft} sq. ft.
    </>
  ) : (
    ''
  );

  const styles = {
    // Simulate overlay on background. Inline styles prevents re-downloading of images
    background: {
      background: `linear-gradient(rgba(47, 60, 83, 0.8), rgba(47, 60, 83, 0.8)), url(${HeroImageWebp.src})`,
    } as React.CSSProperties,
  };

  // Prioritize rent zestimate range from property details API response.
  // In cases where it might not exist, fall back to previous rent range comparable.
  const getMinMaxRentRange = () => {
    return {
      min: rentZestimateRangeLow ? rentZestimateRangeLow : min,
      max: rentZestimateRangeHigh ? rentZestimateRangeHigh : max,
    };
  };

  return (
    <Box css={containerStyles} style={styles.background}>
      <Box css={detailsStyles}>
        <Heading level={1} textStyle="responsive.heading-md" css={{ color: 'text.onHero.neutral' }}>
          {h1Text}
        </Heading>

        {h2Text && (
          <Heading level={2} css={{ color: 'text.onHero.neutral', textStyle: 'responsive.body-lg' }}>
            {h2Text}
          </Heading>
        )}

        <TextButton asChild textStyle="body-sm" onImpact icon={<IconChevronLeftFilled />} css={{ marginTop: '600' }}>
          <a href={`${pathsManifest.PriceMyRentalPage}/${fromApp ? '?fromApp=true' : ''}`}>Try another address</a>
        </TextButton>
      </Box>
      {hasUserPropertyData && (rentZestimate || priceFromFilter) && (
        <ZestimateCard
          min={getMinMaxRentRange().min}
          max={getMinMaxRentRange().max}
          rentZestimate={rentZestimate || null}
          zpid={zpid || null}
          priceFromFilter={priceFromFilter}
        />
      )}
    </Box>
  );
};

export default HeadingCard;
